import {
  Avatar,
  Box,
  Button,
  Card,
  Heading,
  MessageBlock,
  NavList,
  Paragraph,
} from '@deckee/deck-hand';
import React, { FC, MouseEvent, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { captureEvent } from '../../../../../services/event-capture';
import { hasFeatureEnabled } from '../../../../../services/feature';
import {
  getStoredImage,
  getWatercraftImage,
} from '../../../../../services/image';
import { openWithInAppBrowser } from '../../../../../services/in-app-browser';
import { useAppDispatch } from '../../../../../services/store';
import {
  getPrepareLocation,
  postMemberSettings,
  selectCurrentMapCenter,
} from '../../../../../services/store/modules/app-controls';
import { MarketplaceSafetyItem } from '../../../../../services/store/modules/marketplace/models';
import { WaterBodyDefinition } from '../../../../../services/store/modules/prepare/models';
import useIsInBounds from '../../../../hooks/use-is-in-bounds';
import HeaderBar from '../../../../layouts/components/HeaderBar/HeaderBar';
import HeaderBarButton from '../../../../layouts/components/HeaderBarButton/HeaderBarButton';
import IconWithTextLockup from '../../../../lockups/IconWithTextLockup/IconWithTextLockup';
import LogoSpinner from '../../../../shared/_deprecated/components/LogoSpinner/LogoSpinner';
import BlueCircleWrapper from '../../../../shared/BlueCircleWrapper/BlueCircleWrapper';
import EmptyInfoPanel from '../../../../shared/EmptyInfoPanel/EmptyInfoPanel';
import RadioChannelsMessageBlock from '../../../../shared/RadioChannelsMessageBlock/RadioChannelsMessageBlock';
import SheetModal from '../../../../shared/SheetModal/SheetModal';
import {
  close,
  location,
  map,
  radio,
  ReactSvg,
} from '../../../../shared/vector-images';
import useVessels from '../../../Vessels/hooks/use-vessels';
import { useRadioRanges } from '../../hooks/useRadioRanges';
import useRecommendedSafetyGear from '../../hooks/useRecommendedSafetyGear';
import useWaterBodyDefinitions from '../../hooks/useWaterBodyDefinitions';
import SafetyGearBlock from '../SafetyGearBlock/SafetyGearBlock';
import SafetyGearFilterBlock from '../SafetyGearFilterBlock/SafetyGearFilterBlock';

const SafetyGearContainer: FC = () => {
  const theme = useTheme();
  const history = useHistory();
  const dispatch = useAppDispatch();

  const { lat: currentMapLatitude, lon: currentMapLongitude } = useSelector(
    selectCurrentMapCenter,
  );

  const canPrepareAus = useIsInBounds('AUSTRALIA');
  const canPrepareWa = useIsInBounds('WESTERNAUSTRALIA');

  const safetyRecommendationPolygonAvailable = canPrepareAus;

  const [showingWaterBodyDefinitions, setShowingWaterBodyDefinitions] =
    useState(false);
  const { waterBodyDefinitions } = useWaterBodyDefinitions();

  const {
    intendedWatersName,
    latitude: selectedLatitude,
    longitude: selectedLongitude,
  } = useSelector(getPrepareLocation);

  const {
    initialLoadComplete,
    mandatorySafetyItems,
    recommendedSafetyItems,
    allSafetyItems,
  } = useRecommendedSafetyGear();

  const [selectedSafetyItem, setSelectedSafetyItem] = useState<
    MarketplaceSafetyItem | undefined
  >();

  const { radioChannels, isAvailable, isLoading } = useRadioRanges({
    lat: selectedLatitude,
    lon: selectedLongitude,
  });

  const handleSelectSafetyItem = (item: MarketplaceSafetyItem) => {
    captureEvent({
      type: 'prepare: click safety item',
      payload: {
        item,
      },
    });
    setSelectedSafetyItem(item);
  };

  const { selectedVessel } = useVessels({
    selectFirstVessel: true,
  });

  const handleSelectYourLocation = (alternateEventTypeName?: string) => {
    captureEvent({
      type: alternateEventTypeName || 'prepare: click choose intended waters',
      payload: {},
    });

    setShowingWaterBodyDefinitions(true);
    // history.push('/maps/prepare/checklist/choose-location');
  };

  const handleSelectChooseYourLocation = (alternateEventTypeName?: string) => {
    captureEvent({
      type: alternateEventTypeName || 'prepare: click choose location',
      payload: {},
    });

    history.push('/maps/prepare/checklist/choose-location');
  };

  // const handleSelectMoreInformationForWa = (e: MouseEvent<HTMLElement>) => {
  //   e.preventDefault();
  //   e.stopPropagation();

  //   openWithInAppBrowser({
  //     url: 'https://www.transport.wa.gov.au/imarine/safety-equipment-changes.asp',
  //   });
  // };

  const handleSelectVessel = () => {
    if (!selectedVessel) {
      captureEvent({
        type: 'prepare: choose add vessel',
        payload: {},
      });
      history.push('/maps/settings/account/boats/new', {
        returnUrl: '/maps/prepare',
      });
      return;
    }
    captureEvent({
      type: 'prepare: choose vessel',
      payload: {},
    });
    history.push('/maps/vessels/select-vessel', { returnUrl: '/maps/prepare' });
  };

  const errorWithSelectedVessel =
    !selectedVessel ||
    !selectedVessel?.watercraft_type ||
    !selectedVessel?.vessel_length;

  const handleSelectWaterBodyDefinition = (definition: WaterBodyDefinition) => {
    captureEvent({
      type: 'prepare: choose water body definition',
      payload: {
        definition,
        latitude: currentMapLatitude,
        longitude: currentMapLongitude,
      },
    });

    dispatch(
      postMemberSettings({
        waterBodyDefinitionConfig: {
          latitude: currentMapLatitude,
          longitude: currentMapLongitude,
          intendedWatersName: definition.name,
          intendedAreaWaterBodyKey: definition.areaWaterBodyKey,
        },
      }),
    )
      .unwrap()
      .finally(() => {
        setShowingWaterBodyDefinitions(false);
      });
  };

  const getSelectedWatercraftDescription = () => {
    // If there is no selected vessel, the prompt to choose one
    if (!selectedVessel) {
      return <Box color="danger">Add a watercraft</Box>;
    }

    // If there is a selected watercraft
    // ...and it's missing details, show a warning
    if (!selectedVessel?.watercraft_type || !selectedVessel?.vessel_length) {
      return <Box color="danger">Add missing details</Box>;
    }

    // If there is a selectedVessel.name then show the name and type. Otherwise just show the type
    if (selectedVessel?.name) {
      return `${selectedVessel.name} • ${selectedVessel?.watercraft_type}`;
    }
    return selectedVessel?.watercraft_type;
  };

  const handleSelectBuySafetyRecommendation = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();

    captureEvent({
      type: 'prepare: buy safety item',
      payload: {
        selectedSafetyItem,
      },
    });

    if (selectedSafetyItem?.marketplaceUrl) {
      openWithInAppBrowser({ url: selectedSafetyItem.marketplaceUrl });
    }
  };

  return (
    <>
      <SheetModal isOpen={!!showingWaterBodyDefinitions}>
        <HeaderBar
          centerNode="Intended waters"
          rightNode={
            <HeaderBarButton
              icon={close()}
              onClick={() => setShowingWaterBodyDefinitions(false)}
            />
          }
        />
        <Box
          width="100vw"
          maxWidth="450px"
          maxHeight="77vh"
          overflow="auto"
          p="2"
        >
          <NavList>
            {safetyRecommendationPolygonAvailable && (
              <NavList.Item onClick={() => handleSelectChooseYourLocation()}>
                <IconWithTextLockup
                  image={
                    <BlueCircleWrapper iconSize={20}>
                      <ReactSvg
                        toDisplay={map()}
                        options={{
                          fillColor: theme.colors.secondary,
                        }}
                        width={20}
                        height={20}
                      />
                    </BlueCircleWrapper>
                  }
                  heading="Select on the map"
                  description="Drop a pin where you plan to go"
                />
              </NavList.Item>
            )}
            {(waterBodyDefinitions || []).map((wbd) => (
              <NavList.Item
                key={wbd.id}
                onClick={() => handleSelectWaterBodyDefinition(wbd)}
              >
                <IconWithTextLockup
                  image={
                    <BlueCircleWrapper iconSize={20}>
                      <ReactSvg
                        toDisplay={location()}
                        options={{
                          fillColor: theme.colors.secondary,
                        }}
                        width={20}
                        height={20}
                      />
                    </BlueCircleWrapper>
                  }
                  heading={wbd.name}
                  description={wbd.description}
                />
              </NavList.Item>
            ))}
          </NavList>
        </Box>
      </SheetModal>

      <SheetModal isOpen={!!selectedSafetyItem}>
        <HeaderBar
          centerNode="More info"
          rightNode={
            <HeaderBarButton
              icon={close()}
              onClick={() => setSelectedSafetyItem(undefined)}
            />
          }
        />
        <Box width="100vw" maxWidth="450px" p="2">
          <Card py="2" px="3">
            <Heading level="5">{selectedSafetyItem?.name}</Heading>
            <Paragraph>{selectedSafetyItem?.description}</Paragraph>
            {selectedSafetyItem?.marketplaceUrl &&
              hasFeatureEnabled('deckee-shop') && (
                <Button.Group pt="3">
                  <Button wide onClick={handleSelectBuySafetyRecommendation}>
                    Buy now
                  </Button>
                </Button.Group>
              )}
          </Card>
        </Box>
      </SheetModal>
      <Card borderStyle="solid" borderWidth="0.5px" borderColor="#dfdfdf">
        <Card.Heading>Your plans</Card.Heading>
        <Card.Body>
          <NavList edge>
            {hasFeatureEnabled('vhf-radio-ranges') && canPrepareWa ? (
              <SafetyGearFilterBlock
                image={
                  <BlueCircleWrapper iconSize={20}>
                    <ReactSvg
                      toDisplay={location()}
                      options={{
                        fillColor: theme.colors.secondary,
                      }}
                      width={20}
                      height={20}
                    />
                  </BlueCircleWrapper>
                }
                heading="Destination"
                description={intendedWatersName || 'Choose location'}
                onClick={() => handleSelectChooseYourLocation()}
              >
                {selectedLatitude &&
                  selectedLongitude &&
                  `${selectedLatitude?.toFixed(
                    6,
                  )}, ${selectedLongitude?.toFixed(6)}`}
              </SafetyGearFilterBlock>
            ) : (
              <SafetyGearFilterBlock
                image={
                  <BlueCircleWrapper iconSize={20}>
                    <ReactSvg
                      toDisplay={location()}
                      options={{
                        fillColor: theme.colors.secondary,
                      }}
                      width={20}
                      height={20}
                    />
                  </BlueCircleWrapper>
                }
                heading="Intended waters"
                description={intendedWatersName || 'Choose location'}
                onClick={() => handleSelectYourLocation()}
              />
            )}

            <SafetyGearFilterBlock
              error={errorWithSelectedVessel}
              image={
                selectedVessel?.image ? (
                  <Avatar src={getStoredImage(selectedVessel.image, 40)} />
                ) : (
                  getWatercraftImage(selectedVessel?.watercraft_type, 40)
                )
              }
              heading="Watercraft"
              description={getSelectedWatercraftDescription()}
              onClick={handleSelectVessel}
            />
          </NavList>
          <Box mt="3" fontSize="1" color="darkergrey">
            Your checklist is configured by these settings.
          </Box>
        </Card.Body>
      </Card>

      {isAvailable && (
        <Card borderStyle="solid" borderWidth="0.5px" borderColor="#dfdfdf">
          <Card.Heading>Radio coverage</Card.Heading>
          <Card.Body>
            {!radioChannels && !isLoading ? (
              <MessageBlock type="warning" mb="2">
                Radio coverage is not available at your selected destination.
              </MessageBlock>
            ) : (
              <>
                <IconWithTextLockup
                  image={
                    <BlueCircleWrapper>
                      <ReactSvg
                        toDisplay={radio()}
                        options={{
                          fillColor: theme.colors.secondary,
                        }}
                        width={24}
                        height={24}
                      />
                    </BlueCircleWrapper>
                  }
                  heading={`Channels: ${radioChannels ?? ''}`}
                />
                <Box mt="2">
                  <RadioChannelsMessageBlock
                    isLoading={isLoading}
                    messageOnly
                  />
                </Box>
              </>
            )}
          </Card.Body>
        </Card>
      )}

      <Card borderStyle="solid" borderWidth="0.5px" borderColor="#dfdfdf">
        <Card.Heading>Mandatory equipment</Card.Heading>

        <Card.Body>
          {/* Take out, but we're expecting WA to want to put this back in */}
          {/* {initialLoadComplete &&
            (intendedWatersName || '').indexOf('WA') === 0 && (
              <MessageBlock type="warning" mb="2">
                There are upcoming changes to safety equipment requirements in
                WA.{' '}
                <strong>
                  <Box
                    as="a"
                    color="black"
                    display="inline"
                    style={{ textDecoration: 'underline' }}
                    href="https://www.transport.wa.gov.au/imarine/safety-equipment-changes.asp"
                    onClick={handleSelectMoreInformationForWa}
                  >
                    More information
                  </Box>
                </strong>
              </MessageBlock>
            )} */}

          {initialLoadComplete &&
            (intendedWatersName || '').indexOf('USA') === 0 && (
              <MessageBlock type="warning" mb="2">
                You may be required to comply with additional regulations or
                laws specific to the state your watercraft is registered or
                operated.
              </MessageBlock>
            )}
          {!initialLoadComplete && <LogoSpinner />}
          <NavList edge grouped>
            {mandatorySafetyItems.map((item) => (
              <SafetyGearBlock
                key={item.id}
                item={item}
                onClick={handleSelectSafetyItem}
              />
            ))}
          </NavList>
          {/* Only load the message block if the initial load is complete, and there are no safety items */}
          {initialLoadComplete && allSafetyItems.length === 0 && (
            <EmptyInfoPanel
              onClick={() =>
                handleSelectYourLocation(
                  'prepare: click choose intended waters info block',
                )
              }
            >
              <Box fontSize="3">
                Select your intended waters and watercraft to see your mandatory
                equipment list.
              </Box>
              <Box fontWeight="bold" color="primary" fontSize="2" mt="3">
                Choose location
              </Box>
            </EmptyInfoPanel>
          )}
        </Card.Body>
      </Card>
      {!!initialLoadComplete && !!recommendedSafetyItems.length && (
        <Card borderStyle="solid" borderWidth="0.5px" borderColor="#dfdfdf">
          <Card.Heading>Recommended for you</Card.Heading>
          <Card.Body>
            {!initialLoadComplete && <LogoSpinner />}
            <NavList edge grouped>
              {recommendedSafetyItems.map((item) => (
                <SafetyGearBlock
                  key={item.id}
                  item={item}
                  onClick={handleSelectSafetyItem}
                />
              ))}
            </NavList>
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default SafetyGearContainer;
